import { useNavigate } from "react-router-dom"
import { useAppSelector } from "./use-store"
import { selectUserDetails, selectUserRoles } from "../../redux/slices/auth"
import {
  addRecentSearch,
  checkIsBankAdmin,
  checkIsLoanOfficer,
  checkIsSuperAdmin,
  getRecentSearches,
  setRecentSearches,
  parseSearchQuery,
  searchByPrefix
} from "../utils"
import { useState, useEffect, useCallback } from "react"
import bbApi from "../../api"
import { useFetchOrgDetailsQuery } from "../../redux/queries"
import { SearchResult, SearchResultType } from "../../types"

export const useSearchAnything = () => {
  const navigate = useNavigate()
  const roles = useAppSelector(selectUserRoles)

  const isSuperAdmin = checkIsSuperAdmin(roles)
  const isBankAdmin = checkIsBankAdmin(roles)
  const isLoanOfficer = checkIsLoanOfficer(roles)

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isLoadingResults, setIsLoadingResults] = useState<boolean>(false)
  const [query, setQuery] = useState<string>("")
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])

  const user = useAppSelector(selectUserDetails)
  const { data: orgDetails } = useFetchOrgDetailsQuery(
    { id: user?.org?.id ?? "" },
    { skip: !user?.org?.id }
  )

  const [recentSearchesData, setRecentSearchesData] = useState<SearchResult[]>(
    getRecentSearches()
  )

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => {
    setQuery("")
    setIsOpen(false)
  }, [])

  const onFocusInput = useCallback(() => {
    open()
  }, [open])

  const onClickItem = useCallback(
    (item: SearchResult) => {
      addRecentSearch(item)
      setRecentSearchesData(getRecentSearches())

      const link = getLink(item)
      if (link) navigate(link)

      close()
    },
    [close, navigate]
  )

  const onClickResetRecentSearches = useCallback(() => {
    setRecentSearches([])
    setRecentSearchesData([])
  }, [])

  const getLink = (item: SearchResult) => {
    const { type, id } = item
    switch (type) {
      case "integration":
        return `/integrations/${id}`
      case "subscription":
        return `/engagements/${id}`
      case "loan_officer":
        return `/loan-officers/${id}`
      case "brand":
        return `/brands/${id}`
      case "lead":
        return `/leads/${id}`
      default:
        return null
    }
  }

  useEffect(() => {
    const { prefix, value } = parseSearchQuery(query)

    // Loan officers don't have access to prefix helpers
    const effectivePrefix = isLoanOfficer ? null : prefix

    // For prefix searches, require at least 1 character after the colon
    // For regular searches, require at least 3 characters
    const minLength = effectivePrefix ? 1 : 3

    if (value.length >= minLength) {
      setIsLoadingResults(true)

      const searchContext = {
        isLoanOfficer,
        isSuperAdmin,
        isBankAdmin,
        userId: user?.id,
        orgId: orgDetails?.id
      }

      const performSearch = effectivePrefix
        ? searchByPrefix(effectivePrefix, value, searchContext)
        : (() => {
            const params: any = { q_str: query }
            if (isLoanOfficer) {
              params.loid = user?.id
            } else {
              params.org_id = isSuperAdmin ? "" : orgDetails?.id ?? ""
            }
            return bbApi.others.getSearchAnything(params)
          })()

      performSearch
        .then(res => {
          setIsLoadingResults(false)
          if ("status" in res) {
            setSearchResults([])
          } else {
            // Priority map: loan officers and brands first
            const typePriority: Record<SearchResultType, number> = {
              loan_officer: 1,
              brand: 2,
              integration: 3,
              lead: 4,
              subscription: 5
            }

            setSearchResults(
              res.sort((a, b) => {
                const priorityA = typePriority[a.type] || 999
                const priorityB = typePriority[b.type] || 999

                // Sort by priority first
                if (priorityA !== priorityB) {
                  return priorityA - priorityB
                }

                // If same priority, sort alphabetically by type
                return a.type > b.type ? 1 : -1
              })
            )
          }
        })
        .catch(() => {
          setIsLoadingResults(false)
          setSearchResults([])
        })
    } else {
      setSearchResults([])
    }
  }, [
    isBankAdmin,
    isLoanOfficer,
    isSuperAdmin,
    orgDetails?.id,
    query,
    user?.id
  ])

  return {
    isLoadingResults,
    isOpen,
    open,
    close,
    onFocusInput,
    onClickItem,
    onClickResetRecentSearches,
    query,
    recentSearchesData,
    searchResults,
    setQuery
  }
}
